'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useAuth } from '@morfi/backend-sdk-v2/client';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { type OutcomeMarketData, PositionCard } from '@/components/market/position-card';
import { mergePositionCardRows } from '@/components/market/position-panel-rows';
import { useMarketPositions } from '@/hooks/positions/use-market-positions';
import { useUserMarketPortfolioPositions } from '@/hooks/positions/user-portfolio/selectors';
import { useConfirmedPositionRows } from '@/hooks/positions/user-portfolio/use-confirmed-position-rows';
import { logger } from '@/lib/telemetry/logger';
import type { OnchainTradeData } from '@/queries/onchain-trade-data';
import { useChartSettingsStore } from '@/stores/chart-settings';
interface WinnerPositionPanelProps {
  /** The winning-outcome SPL mint this winner page canonicalises on. */
  winnerMint: string;
  /** Original outcome index retained by the graduated winner token. */
  outcomeIndex: number;
  /** Current market cap from the canonical winner response. */
  effectiveMarketCapD6: string;
  /** Indexed winner supply used for average-entry market-cap conversion. */
  indexedOutcomeMintSupplyD6: string;
  /** Exact pool state shared with the locked winner trade form. */
  tradeData: OnchainTradeData;
  className?: string;
}

/**
 * Winner-page "Your position" panel (mockup #02). Scopes the canonical
 * `<PositionCard>` to the winning outcome mint so the row comes back
 * `positionType: winner` and prices off the graduated AMM. No `winnerHref`:
 * the user is already on the trade surface, so the won state shows value +
 * prize note + buy/sell chips rather than a "Trade winner" link. Renders
 * nothing until a wallet is connected; `<PositionCard>` itself returns null
 * when the user holds none of the winner.
 */
export function WinnerPositionPanel(t0) {
  const $ = _c(45);
  const {
    winnerMint,
    outcomeIndex,
    effectiveMarketCapD6,
    indexedOutcomeMintSupplyD6,
    tradeData,
    className
  } = t0;
  const {
    activeWallet
  } = useActiveWallet();
  const {
    user
  } = useAuth();
  const wallet = winnerMint === "" ? "" : activeWallet?.address ?? "";
  const t1 = user?.id ?? "";
  let t2;
  if ($[0] !== winnerMint) {
    t2 = winnerMint === "" ? [] : [winnerMint];
    $[0] = winnerMint;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== wallet) {
    t3 = {
      userId: t1,
      wallet,
      outcomeMints: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = wallet;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    rows: snapshotRows
  } = useUserMarketPortfolioPositions(t3);
  let t4;
  if ($[6] !== wallet) {
    t4 = wallet === "" ? [] : [wallet];
    $[6] = wallet;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== winnerMint) {
    t5 = winnerMint === "" ? [] : [winnerMint];
    $[8] = winnerMint;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== wallet) {
    t6 = {
      userId: wallet,
      walletPubkeys: t4,
      outcomeMints: t5
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = wallet;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const indexedPositions = useMarketPositions(t6);
  let t7;
  if ($[14] !== indexedPositions.rows || $[15] !== snapshotRows || $[16] !== wallet || $[17] !== winnerMint) {
    t7 = mergePositionCardRows({
      snapshotRows,
      indexedRows: indexedPositions.rows,
      wallet,
      outcomeMint: winnerMint
    });
    $[14] = indexedPositions.rows;
    $[15] = snapshotRows;
    $[16] = wallet;
    $[17] = winnerMint;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const indexedRows = t7;
  const t8 = user?.id ?? "";
  let t9;
  if ($[19] !== tradeData) {
    t9 = tradeData === undefined ? {} : {
      tradeData
    };
    $[19] = tradeData;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== indexedRows || $[22] !== t8 || $[23] !== t9 || $[24] !== wallet || $[25] !== winnerMint) {
    t10 = {
      userId: t8,
      wallet,
      rows: indexedRows,
      outcomeMint: winnerMint,
      ...t9
    };
    $[21] = indexedRows;
    $[22] = t8;
    $[23] = t9;
    $[24] = wallet;
    $[25] = winnerMint;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const rows = useConfirmedPositionRows(t10);
  const marketCapMode = useChartSettingsStore(_temp);
  let t11;
  if ($[27] !== effectiveMarketCapD6 || $[28] !== indexedOutcomeMintSupplyD6) {
    t11 = {
      effectiveMarketCapD6,
      indexedOutcomeMintSupplyD6
    };
    $[27] = effectiveMarketCapD6;
    $[28] = indexedOutcomeMintSupplyD6;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== outcomeIndex || $[31] !== t11) {
    t12 = {
      [outcomeIndex]: t11
    };
    $[30] = outcomeIndex;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const outcomeMarket = t12;
  let t13;
  let t14;
  if ($[33] !== indexedPositions.isError || $[34] !== winnerMint) {
    t13 = () => {
      if (!indexedPositions.isError) {
        return;
      }
      logger.warn("winner.position_history_failed", {
        winnerMint
      });
    };
    t14 = [indexedPositions.isError, winnerMint];
    $[33] = indexedPositions.isError;
    $[34] = winnerMint;
    $[35] = t13;
    $[36] = t14;
  } else {
    t13 = $[35];
    t14 = $[36];
  }
  useEffect(t13, t14);
  if (wallet === "") {
    return null;
  }
  let t15;
  if ($[37] !== className) {
    t15 = className !== undefined ? {
      className
    } : {};
    $[37] = className;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== marketCapMode || $[40] !== outcomeMarket || $[41] !== rows || $[42] !== t15 || $[43] !== tradeData) {
    t16 = <PositionCard rows={rows} winnerPage={true} marketCapMode={marketCapMode} outcomeMarket={outcomeMarket} tradeData={tradeData} {...t15} />;
    $[39] = marketCapMode;
    $[40] = outcomeMarket;
    $[41] = rows;
    $[42] = t15;
    $[43] = tradeData;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
}
function _temp(state) {
  return state.yAxisProperty === "marketCap";
}